import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { clearToken, getDisplayName } from '../lib/auth';
import { fetchQueues } from '../lib/api';
import { fetchSupportTickets } from '../lib/support-api';
import {
  HOME_PATH,
  NAV_FOOTER,
  NAV_GROUPS,
  badgeText,
  type NavBadge,
  type NavEntry,
} from '../lib/navigation';
import { useLocale } from '../i18n/LocaleProvider';
import { Icon, Wordmark } from './Brand';
import { LanguageSwitcher } from './LanguageSwitcher';

/** A badge as {@link NavItem} draws it. */
interface BadgeView {
  readonly text: string;
  /** The count as a sentence, for a screen reader — "5" alone says nothing. */
  readonly label: string;
  /** `alert` when something is late or broken, `neutral` when it is merely waiting. */
  readonly tone: 'alert' | 'neutral';
}

/**
 * The live counts behind the sidebar's badges.
 *
 * Both reads are ones the dashboard already makes: the queue summary is what Systemstatus polls
 * (same query key, so the two share one request), and the ticket counts ride on the queue list's
 * own response — `counts` is independent of filters, so a one-row page is enough to get them.
 * A failed read shows no badge rather than an error: the sidebar is not where an outage is told.
 */
function useNavBadges(): Partial<Record<NavBadge, BadgeView>> {
  const { tCount } = useLocale();

  const queues = useQuery({
    queryKey: ['queues'],
    queryFn: fetchQueues,
    refetchInterval: 30_000,
    retry: false,
  });
  const tickets = useQuery({
    // Under the queue's `support-tickets` prefix, so the ticket page's refresh after a reply or a
    // status change updates the badge too, instead of leaving it stale until the next poll.
    queryKey: ['support-tickets', 'nav-counts'],
    queryFn: () => fetchSupportTickets({ limit: 1 }),
    refetchInterval: 30_000,
    retry: false,
  });

  const badges: Partial<Record<NavBadge, BadgeView>> = {};

  const open = tickets.data?.counts['open'];
  const openText = badgeText(open);
  if (openText && open !== undefined) {
    badges.openTickets = {
      text: openText,
      label: tCount('shell.badge.openTickets', open),
      tone: (tickets.data?.counts['overdue'] ?? 0) > 0 ? 'alert' : 'neutral',
    };
  }

  const failed = queues.data?.queues.reduce(
    (sum, queue) => sum + queue.failed,
    0,
  );
  const failedText = badgeText(failed);
  if (failedText && failed !== undefined) {
    badges.failedJobs = {
      text: failedText,
      label: tCount('shell.badge.failedJobs', failed),
      tone: 'alert',
    };
  }

  return badges;
}

/**
 * The nav link recipe, derived from `Navigation.astro`'s `linkClass`: the active item is
 * `text-primary-700 bg-primary-50` on the marketing site, and stays that here rather than the
 * solid blue block it used to be. A tool's sidebar has one active item at a time and the tinted
 * treatment is enough to find it — a filled bar competes with the page's own primary button.
 */
function NavItem({
  entry,
  badge,
  onClick,
}: {
  readonly entry: NavEntry;
  readonly badge?: BadgeView;
  readonly onClick?: () => void;
}) {
  const { t } = useLocale();
  return (
    <NavLink
      to={entry.to}
      end={entry.to === '/'}
      onClick={onClick}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 px-3 rounded-lg text-[15px] font-medium transition-colors min-h-[44px]',
          isActive
            ? 'text-primary-700 bg-primary-50'
            : 'text-gray-700 hover:text-primary-700 hover:bg-gray-50',
        )
      }
    >
      <Icon name={entry.icon} className="h-5 w-5 shrink-0" />
      <span className="truncate">{t(entry.labelKey)}</span>
      {badge ? (
        <span
          className={clsx(
            'ml-auto shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
            badge.tone === 'alert'
              ? 'bg-red-100 text-red-700'
              : 'bg-gray-100 text-gray-700',
          )}
        >
          <span aria-hidden="true">{badge.text}</span>
          <span className="sr-only">{badge.label}</span>
        </span>
      ) : null}
    </NavLink>
  );
}

/**
 * The frame every signed-in screen shares.
 *
 * The header is the landing page's, recipe for recipe — sticky, `bg-white/95 backdrop-blur`,
 * wordmark on the left, identity and sign-out on the right — the same one `PortalShell` carries,
 * so the three surfaces of the product open the same way.
 *
 * The sidebar is what the portal does NOT have, and it stays: this tool has a dozen destinations an
 * operator moves between mid-incident, and burying them behind a menu to match a marketing site
 * with five would be copying the look at the cost of the job. It is restyled, not removed.
 *
 * What it lists, in which group and order, is data (`lib/navigation.ts`) — this component only
 * draws it: labelled groups on top, the rare and personal entries pinned to the bottom.
 */
export function Layout() {
  const { t, tNode } = useLocale();
  const displayName = getDisplayName();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const badges = useNavBadges();

  function handleLogout() {
    clearToken();
    navigate('/login');
  }

  function closeSidebar() {
    setSidebarOpen(false);
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/*
        Full-bleed gutters rather than `container-custom`, unlike the marketing header. The
        sidebar starts at the viewport edge, so a max-width header would leave the wordmark
        floating somewhere to the right of the nav it labels on a wide screen.
      */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setSidebarOpen((open) => !open)}
                className="lg:hidden flex shrink-0 items-center justify-center w-11 h-11 -ml-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label={t(
                  sidebarOpen ? 'shell.closeMenu' : 'shell.openMenu',
                )}
                aria-expanded={sidebarOpen}
              >
                <Icon
                  name={sidebarOpen ? 'close' : 'menu'}
                  className="h-6 w-6"
                />
              </button>

              <NavLink
                to={HOME_PATH}
                className="flex items-center gap-2.5 shrink-0"
                aria-label="Easy Invoices Ops"
              >
                <Wordmark compact />
                <span className="eyebrow hidden sm:inline-block ml-3">Ops</span>
              </NavLink>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              {/* The switcher sits in the header on every signed-in screen, and on the sign-in
                  screen too: an operator who cannot read the form cannot get far enough to
                  reach a header. */}
              <LanguageSwitcher />
              {/* Which operator is signed in. With several accounts sharing one browser this
                  is what stops a support reply going out signed by whoever signed in last —
                  every agent reply is attributed to this identity server-side. From `lg` only:
                  on a tablet a long name pushed the switcher over the wordmark. */}
              {displayName ? (
                <span className="text-sm text-gray-600 hidden lg:inline truncate max-w-[16rem]">
                  {tNode('shell.signedInAs', {
                    name: (
                      <span className="font-medium text-gray-900">
                        {displayName}
                      </span>
                    ),
                  })}
                </span>
              ) : null}
              {/* Icon-only on a phone: hamburger, wordmark, switcher and a labelled button do not
                  fit 375px, and the wordmark is what got pushed under the switcher. */}
              <button
                type="button"
                onClick={handleLogout}
                className="btn-secondary btn-sm gap-2 max-sm:h-11 max-sm:w-11 max-sm:px-0"
              >
                <Icon name="logout" className="h-4 w-4" />
                <span className="sr-only sm:not-sr-only">
                  {t('shell.signOut')}
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Backdrop when the sidebar is open on mobile */}
        {sidebarOpen && (
          <button
            type="button"
            onClick={closeSidebar}
            className="lg:hidden fixed inset-0 top-16 bg-gray-900/40 z-40"
            aria-label={t('shell.closeMenu')}
          />
        )}

        <aside
          className={clsx(
            'w-64 shrink-0 bg-white border-r border-gray-200 z-40 transition-transform duration-200 ease-out',
            'fixed lg:sticky top-16 bottom-0 left-0 lg:top-16 lg:h-[calc(100vh-4rem)] overflow-y-auto',
            sidebarOpen
              ? 'translate-x-0'
              : '-translate-x-full lg:translate-x-0',
          )}
        >
          {/* A column that is at least as tall as the sidebar, so the footer group sits at the
              bottom on a tall screen and simply follows the groups on a short one. */}
          <nav
            className="flex min-h-full flex-col px-3 py-4"
            aria-label={t('shell.sections')}
          >
            {/* Below `lg` the header has no room for the identity, so the menu carries it: the
                question "who am I about to reply as" still has an answer on a phone. */}
            {displayName ? (
              <p className="lg:hidden px-3 pb-4 mb-4 border-b border-gray-200 text-sm text-gray-600 break-words">
                {tNode('shell.signedInAs', {
                  name: (
                    <span className="font-medium text-gray-900">
                      {displayName}
                    </span>
                  ),
                })}
              </p>
            ) : null}
            <div className="space-y-6 pb-6">
              {NAV_GROUPS.map((group) => (
                <section key={group.id} aria-labelledby={`nav-${group.id}`}>
                  <h2 id={`nav-${group.id}`} className="eyebrow px-3 pb-2">
                    {t(group.labelKey)}
                  </h2>
                  <ul className="space-y-1">
                    {group.entries.map((entry) => (
                      <li key={entry.to}>
                        <NavItem
                          entry={entry}
                          badge={entry.badge ? badges[entry.badge] : undefined}
                          onClick={closeSidebar}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            <ul className="mt-auto space-y-1 border-t border-gray-200 pt-4">
              {NAV_FOOTER.map((entry) => (
                <li key={entry.to}>
                  <NavItem entry={entry} onClick={closeSidebar} />
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>

      <footer className="bg-white border-t border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8 py-5 text-sm text-gray-500 flex flex-wrap items-center justify-between gap-2">
          <span>
            {t('shell.footer.copyright', { year: new Date().getFullYear() })}
          </span>
          <span className="text-gray-400">{t('shell.footer.audit')}</span>
        </div>
      </footer>
    </div>
  );
}
